import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { DateRange } from "react-date-range";
import { format } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./TransactionSummary.css";
import { getSummary, getSummaryData } from "../../../config/LiveApi";
import { useSelector } from "react-redux";

function TransactionSummary() {
  const token = useSelector((store) => store.userReducer.token);

  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [progress, setProgress] = useState([]);

  const pickerRef = useRef(null);

  const [dateRange, setDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  const getUtcRangeFromIST = () => {
    // Start date - subtract 1 day
    const start = new Date(dateRange[0].startDate);
    start.setDate(start.getDate());
    start.setHours(0, 0, 0, 0);

    // End date - same day till end
    const end = new Date(dateRange[0].endDate);
    end.setHours(23, 59, 59, 999);

    return {
      fromDate: start.toISOString(),
      toDate: end.toISOString(),
    };
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const { fromDate, toDate } = getUtcRangeFromIST();

      const res = await getSummary(token, fromDate, toDate);

      setSummary(res.data.data || []);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const downloadSingleCard = async (row) => {
    try {
      const { fromDate, toDate } = getUtcRangeFromIST();
      const params = { fromDate, toDate, orgName: row.orgName, pg: row.pg };

      const res = await getSummaryData(token, params);

      const records = res.data.data || [];
      if (!records.length) return alert("No transactions found");

      const excelData = records.map((tx) => ({
        OrgName: row.orgName,
        PG: row.pg,
        ReferenceID: tx.referenceId,
        Name: tx.name,
        Status: tx.responseStatus,
        UTR: tx.responseUTR,
        Amount: tx.requestAmount,
        Type: tx.type,
        TransactionID: tx.transactionId,
        CreatedAt_IST: format(
          new Date(tx.createdAt_IST),
          "dd MMM yyyy HH:mm:ss"
        ),
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");

      XLSX.writeFile(wb, `${row.orgName}_${row.pg}_transactions.xlsx`);
    } catch (err) {
      console.error(err);
      alert(`Failed to download transactions for ${row.orgName} - ${row.pg}`);
    }
  };

  /* ================= DOWNLOAD ALL SEPARATE EXCELS ================= */
  const downloadAll = async () => {
    if (!summary.length) return;
    const { fromDate, toDate } = getUtcRangeFromIST();

    // Initialize progress
    const newProgress = summary.map((row) => ({
      orgName: row.orgName,
      pg: row.pg,
      status: "pending",
    }));
    setProgress(newProgress);

    for (let i = 0; i < summary.length; i++) {
      const row = summary[i];
      try {
        const res = await axios.get(DOWNLOAD_API_URL, {
          params: { fromDate, toDate, orgName: row.orgName, pg: row.pg },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const txns = res.data?.data || [];
        if (!txns.length) throw new Error("No transactions");

        const excelData = txns.map((tx) => ({
          OrgName: row.orgName,
          PG: row.pg,
          ReferenceID: tx.referenceId,
          Name: tx.name,
          Status: tx.responseStatus,
          UTR: tx.responseUTR,
          Amount: tx.requestAmount,
          Type: tx.type,
          TransactionID: tx.transactionId,
          CreatedAt_IST: format(
            new Date(tx.createdAt_IST),
            "dd MMM yyyy HH:mm:ss"
          ),
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transactions");

        XLSX.writeFile(wb, `${row.orgName}_${row.pg}_transactions.xlsx`);

        // Update progress
        setProgress((prev) =>
          prev.map((p) =>
            p.orgName === row.orgName && p.pg === row.pg
              ? { ...p, status: "done" }
              : p
          )
        );
      } catch (err) {
        console.error(err);
        setProgress((prev) =>
          prev.map((p) =>
            p.orgName === row.orgName && p.pg === row.pg
              ? { ...p, status: "failed" }
              : p
          )
        );
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="summary-container">
      <h2 className="summary-title">📊 Transaction Summary</h2>

      {/* FILTER */}
      <div className="filter-card">
        <div className="date-range-wrapper">
          <label>Date Range</label>
          <div
            className="date-input"
            onClick={() => setShowPicker(!showPicker)}
          >
            {format(dateRange[0].startDate, "dd MMM yyyy")} —{" "}
            {format(dateRange[0].endDate, "dd MMM yyyy")}
          </div>

          {showPicker && (
            <div className="calendar-popover" ref={pickerRef}>
              <DateRange
                ranges={dateRange}
                onChange={(item) => {
                  setDateRange([item.selection]);
                  setShowPicker(false);
                }}
                moveRangeOnFirstSelection={false}
                rangeColors={["#fc8019"]}
              />
            </div>
          )}
        </div>

        <button
          className="primary-btn"
          onClick={fetchSummaryData}
          disabled={loading}
        >
          {loading ? "Fetching..." : "Fetch Data"}
        </button>
      </div>

      {/* CARDS */}
      {summary.length > 0 && (
        <>
          <div className="stats-grid">
            {summary.map((row, idx) => {
              const isPayout = row.pg?.toLowerCase().includes("payout");
              const rowProgress = progress.find(
                (p) => p.orgName === row.orgName && p.pg === row.pg
              );

              return (
                <div className="stat-card" key={idx}>
                  <div className="card-header">
                    <h4>
                      {row.orgName}
                      <span>{row.pg}</span>
                    </h4>

                    <button
                      className="card-download-btn"
                      onClick={() => downloadSingleCard(row)}
                    >
                      <span className="download-icon">⬇</span>
                      <span className="download-ring" />
                    </button>
                  </div>

                  <div className="card-body">
                    <p>
                      Total <strong>{row.total}</strong>
                    </p>
                    <p>
                      {isPayout ? "Settled" : "Success"}{" "}
                      <strong>{row.success}</strong>
                    </p>
                    <p>
                      {isPayout ? "Settlement Failed" : "Failed"}{" "}
                      <strong>{row.failed}</strong>
                    </p>
                    <p>
                      {isPayout ? "Settlement Processing" : "Processing"}{" "}
                      <strong>{row.processing}</strong>
                    </p>

                    {rowProgress && (
                      <p>
                        Download Status:{" "}
                        <strong>
                          {rowProgress.status === "pending"
                            ? "Pending"
                            : rowProgress.status === "done"
                              ? "✅ Done"
                              : "❌ Failed"}
                        </strong>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="download-wrapper">
            <button className="download-btn" onClick={downloadAll}>
              ⬇ Download All Transactions (Separate Excel)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default TransactionSummary;
